import { GridRowParams } from "@mui/x-data-grid-premium";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback, useMemo, useState } from "react";

interface WasteStreamRow {
  id: string;
  classification?: string | null | undefined;
  federalWasteCodes?: string[] | null | undefined;
  stateWasteCodes?: string[] | null | undefined;
  formCode?: string | null | undefined;
  sourceCode?: string | null | undefined;
}

interface UseWasteStreamSelectionReturn<T extends WasteStreamRow> {
  selectedRows: T[];
  setSelectedRows: (rows: T[]) => void;
  isRowSelectable: ((params: GridRowParams<T>) => boolean) | undefined;
  canMergeSelected: boolean;
}

// Limit row selection to 3
const MAX_SELECTION = 3;

/**
 * Hook to manage waste stream selection with merge validation.
 *
 * Ensures that only compatible waste streams can be selected together.
 * Compatible streams must have identical:
 * - classification
 * - federalWasteCodes
 * - stateWasteCodes
 * - formCode (or one can be blank)
 * - sourceCode (or one can be blank)
 */
export function useWasteStreamSelection<
  T extends WasteStreamRow
>(): UseWasteStreamSelectionReturn<T> {
  const { isStaff } = useCurrentUser();
  const [selectedRows, setSelectedRows] = useState<T[]>([]);

  // Helper function to check if two waste streams are merge compatible
  const areMergeCompatible = useCallback((stream1: T, stream2: T): boolean => {
    // Check if arrays are equal (order doesn't matter)
    const arraysEqual = (
      a: string[] | null | undefined,
      b: string[] | null | undefined
    ) => {
      const arrayA = a || [];
      const arrayB = b || [];
      if (arrayA.length !== arrayB.length) return false;
      const sortedA = [...arrayA].sort();
      const sortedB = [...arrayB].sort();
      return sortedA.every(
        (val, i) =>
          val.toLocaleLowerCase().trim() === sortedB[i].toLowerCase().trim()
      );
    };

    // Check if a string value is blank (null, undefined, or empty after trimming)
    const isBlank = (value: string | null | undefined): boolean => {
      return value == null || value.trim() === "";
    };

    // Check if two string codes are compatible for merging
    // Compatible if: both are the same, OR one is blank and the other has a value
    const codesCompatible = (
      code1: string | null | undefined,
      code2: string | null | undefined
    ): boolean => {
      const blank1 = isBlank(code1);
      const blank2 = isBlank(code2);

      // If either is blank, they are compatible
      if (blank1 || blank2) {
        return true;
      }

      // Both have values, check if they match
      return code1?.toLowerCase().trim() === code2?.toLowerCase().trim();
    };

    return (
      // Same classification
      stream1.classification === stream2.classification &&
      // Same federal waste codes
      arraysEqual(stream1.federalWasteCodes, stream2.federalWasteCodes) &&
      // Same state waste codes
      arraysEqual(stream1.stateWasteCodes, stream2.stateWasteCodes) &&
      // Form code: same or one is blank
      codesCompatible(stream1.formCode, stream2.formCode) &&
      // Source code: same or one is blank
      codesCompatible(stream1.sourceCode, stream2.sourceCode)
    );
  }, []);

  const isRowSelectable = useMemo(() => {
    if (!isStaff) return undefined;

    return (params: GridRowParams<T>) => {
      const row = params.row;
      if (!row) return false;

      // If this row is already selected, it's always selectable (for deselection)
      if (selectedRows.some((selected) => selected.id === row.id)) {
        return true;
      }

      // Maximum selections reached
      if (selectedRows.length >= MAX_SELECTION) {
        return false;
      }

      // If no rows selected yet, this one is selectable
      if (selectedRows.length === 0) {
        return true;
      }

      // Check if this row is merge compatible with all selected rows
      return selectedRows.every((selected) =>
        areMergeCompatible(row, selected)
      );
    };
  }, [isStaff, selectedRows, areMergeCompatible]);

  // Check if current selection can be merged (needs at least 2 items)
  const canMergeSelected = useMemo(() => {
    return selectedRows.length >= 2;
  }, [selectedRows]);

  return {
    selectedRows,
    setSelectedRows,
    isRowSelectable,
    canMergeSelected,
  };
}
